import {
	Component,
	OnInit,
	OnDestroy,
	Input,
	Output,
	EventEmitter,
	TemplateRef,
	ViewChild,
	HostBinding,
	AfterViewInit
} from "@angular/core";

import { IdService } from "../shared/services/id.service";

@Component({
	selector: "rbc-button",
	template: `
		<ng-container
			*ngIf="link; else buttonLink">
			<a
				[id]="id"
				[attr.href]="link"
				[attr.target]="linkTarget"
				[attr.disabled]= "disabled ? disabled : null"
				[attr.aria-disabled]="disabled"
				[ngClass]="classGen"
				(click)="!disabled ? buttonClick.emit($event): $event.preventDefault()"
				(mouseover)="buttonMouseOver.emit($event)"
				(mouseout)="buttonMouseOut.emit($event)"
				(focus)="buttonFocus.emit($event)">
				<ng-container *ngTemplateOutlet="buttonContents"></ng-container>
			</a>
		</ng-container>

		<ng-template #buttonLink>
			<ng-container
				*ngIf="routerLink; else button">
				<button
					[id]= "id"
					[ngClass]= "classGen"
					[disabled]= "disabled ? disabled : null"
					[attr.aria-disabled]="disabled"
					[attr.type]= "type == 'submit' || type == 'reset' || type == 'button' ? type : null"
					[routerLink]="['/'+ routerLink] || ['']"
					routerLinkActive="active"
					(click)="!disabled ? buttonClick.emit($event): $event.preventDefault()"
					(mouseover)= "buttonMouseOver.emit($event)"
					(mouseout)= "buttonMouseOut.emit($event)"
					(focus)="buttonFocus.emit($event)"
					[attr.aria-label]="ariaLabel">
					<ng-container *ngTemplateOutlet="buttonContents"></ng-container>
				</button>
			</ng-container>

			<ng-template #button>
			<button
				[id]= "id"
				[ngClass]= "classGen"
				[attr.aria-disabled]="disabled"
				[disabled]= "disabled"
				[attr.type]= "type == 'submit' || type == 'reset' || type == 'button' ? type : null"
				(click)="!disabled ? buttonClick.emit($event): $event.preventDefault()"
				(mouseover)= "buttonMouseOver.emit($event)"
				(mouseout)= "buttonMouseOut.emit($event)"
				(focus)="buttonFocus.emit($event)"
				[attr.aria-label]="ariaLabel">
				<ng-container *ngTemplateOutlet="buttonContents"></ng-container>
			</button>
			</ng-template>
		</ng-template>

		<ng-template #buttonContents>
			<ng-container *ngIf="!loading || loadingType === 'inline'">
				<rbc-icon
					[id]="id+'Icon'"
					[ngClass]="{ 'rbc-button-chev': chevron, 'rbc-button-icon-right': chevron || iconPositionX === 'right' }"
					*ngIf="icon"
					[icon]="icon"
					[type]="iconWeight"
					[attr.aria-hidden]="true"></rbc-icon>
				<slot #default><ng-content></ng-content></slot>

			</ng-container>
			<ng-container *ngIf="loading">
				<rbc-spinner></rbc-spinner>
			</ng-container>
		</ng-template>

	`,
	styles: []
})
export class ButtonComponent implements OnInit, OnDestroy, AfterViewInit {
	// Private Members
	@Input() id: string;
	isLoading = false;
	width: string;
	@Input() ariaLabel;
	// Input Parameters
	@Input() type:
		| ""
		| "button"
		| "primary"
		| "primary-outline"
		| "secondary"
		| "secondary-outline"
		| "white-outline"
		| "success"
		| "success-outline"
		| "danger"
		| "danger-outline"
		| "warning"
		| "warning-outline"
		| "chev"
		| "chev-up"
		| "chev-down"
		| "none"
		| "submit"
		| "reset" = "";

	@Input() color:
		| "primary"
		| "secondary"
		| "dark"
		| "light"
		| "success"
		| "danger"
		| "warning"
		| "white"
		| "transparent";

	@Input() outline;
	@Input() chevron: "right" | "up" | "down";
	@Input() iconPositionX: "left"| "center" | "right" = "left";
	@Input() disabled = false;
	@Input() size: "large" | "medium" | "small" = "medium";
	@Input() fullWidth = false;
	@Input() fullWidthMobile = false;
	@Input() icon = "";
	@Input() iconWeight: "solid" | "light" = "light";
	@Input() link: string;
	@Input() linkTarget: "_blank" | "_self" | "_parent" | "_top" | "" = "";
	@Input() routerLink: string;
	@Input() get loading() {
		return this.isLoading;
	}

	set loading(v: boolean) {
		this.isLoading = v;
	}

	@Input() loadingType: "full" | "inline" = "full";

	@Output() buttonClick = new EventEmitter();
	@Output() buttonFocus = new EventEmitter();
	@Output() buttonMouseOver = new EventEmitter();
	@Output() buttonMouseOut = new EventEmitter();

	@ViewChild("buttonContents", { static: false }) ref: TemplateRef<any>;
	@HostBinding("class.rbc-button-block") hostWidth = false;
	@HostBinding("class.rbc-button-block-xs") hostWidthMobile = false;
	@ViewChild("default", { static: false }) $defaultContent;

	defaultContentPresent = true;

	get classGen() {
		const classes = {};
		const color = this.color ?
			this.color :
			(this.type && this.type !== "submit" && this.type !== "reset" && this.type !== "none" && this.type !== "button") ?
				this.type :
				"primary";

		classes["rbc-button"] = true;
		classes[`rbc-button-${this.size}`] = this.size;
		classes[`rbc-button-${color}`] = color && !this.outline;
		classes[`rbc-button-${color}-outline`] = this.outline;
		classes[`rbc-button-chev-${this.chevron}`] =
			this.chevron === "up" || this.chevron === "down";
		classes["rbc-button-chev"] = this.chevron === "right";
		classes["rbc-button-block"] = this.fullWidth;
		classes["rbc-button-block-xs"] = this.fullWidthMobile;
		classes["loading"] = this.isLoading;
		classes["rbc-button-loading-full"] =
			this.isLoading && this.loadingType === "full";
		classes["rbc-button-loading-inline"] =
			this.isLoading && this.loadingType === "inline";
		classes["rbc-button-icon"] =
			(this.icon || this.chevron) && !this.defaultContentPresent;
		classes[`rbc-button-${this.type}`] =
			this.type && this.type !== "submit" && this.type !== "reset";

		return classes;
	}

	constructor(private idService: IdService) {
		this.id = idService.generate();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	ngOnInit() {
		this.hostWidth = this.fullWidth;
		this.hostWidthMobile = this.fullWidthMobile;
		this.isLoading = this.loading;
	}

	ngAfterViewInit() {
		if (this.$defaultContent) {
				this.$defaultContent.nativeElement.childNodes.length > 0 ? this.defaultContentPresent = true : this.defaultContentPresent = false;
		}
	}

	click(event){
		this.buttonClick.emit(event)
	}
}
